/**
 * This is the starting point of your application.
 * Feature Hub Apps will use this file to bootstrap the app.
 */
import React, { useState, useEffect } from 'react';
import {
  ApolloProvider,
  ApolloClient,
  InMemoryCache,
  createHttpLink,
  ApolloLink,
} from '@apollo/client';
import { concat } from '@apollo/client/link/core';
import { I18nextProvider } from 'react-i18next';
import { useDispatch } from 'react-redux';
import {
  ContentServiceV1 as ContentService,
  ContentChangeListener,
} from '@volkswagen-onehub/audi-content-service';
import { ThemeProvider } from 'styled-components';
import { GfaServiceConfigProviderV1 } from '@volkswagen-onehub/gfa-service-config-provider';
import type { TrackingServiceV2 } from '@oneaudi/audi-tracking-service';
import { createTrackingService } from '@volkswagen-onehub/oneaudi-os-adapter-tracking';
import { v4 as uuidv4 } from 'uuid';
import { AppContent, FalconContent, mapContent } from './ContentMapping/contentMapping';
import { GRAPHQL_GATEWAY_URL } from './constants/environment';
import VehicleProvider from './components/VehicleProvider';
import i18n from '../i18n';
import GlobalStyles from './styles';
import { COLORS } from './styles/variables';
import ContentWrapper from './components/ContentWrapper';
import { setPriceBreakdowns } from './redux/priceBreakdownReducer';
import ErrorBoundary from './components/ErrorBoundary';
import { IS_T3_WEBSITE } from './constants';
import mockContentFromHeadless from './constants/mockContentDefault.json';

interface FeatureAppProps {
  configProvider?: GfaServiceConfigProviderV1;
  trackingService?: TrackingServiceV2;
  contentService?: ContentService;
}

const correlationIdLink = new ApolloLink((operation, forward) => {
  operation.setContext(({ headers = {} }) => ({
    headers: {
      ...headers,
      'x-correlation-id': uuidv4(),
    },
  }));

  return forward(operation);
});

const httpLink = createHttpLink({
  uri: GRAPHQL_GATEWAY_URL,
});

const apolloClient = new ApolloClient({
  link: concat(correlationIdLink, httpLink),
  cache: new InMemoryCache(),
  name: __FEATURE_APP_NAME__,
  version: __FEATURE_APP_VERSION__,
});

const FeatureApp: React.FC<FeatureAppProps> = ({
  trackingService = createTrackingService(__FEATURE_APP_NAME__),
  contentService,
}: FeatureAppProps) => {
  const dispatch = useDispatch();

  const [rawContent, setRawContent] = useState(contentService?.getContent() as AppContent);
  console.log('🚀 ~ FA_VDP_PRICING_BREAKDOWN => rawContent:', rawContent);

  React.useEffect(() => {
    // eslint-disable-next-line no-param-reassign
    trackingService.featureAppName = __FEATURE_APP_NAME__;
    const readyEvent = {
      event: {
        attributes: {
          componentName: __FEATURE_APP_NAME__,
          elementName: '',
          label: '',
          clickID: '',
          pos: '',
          targetURL: '',
          value: '',
        },
        eventInfo: {
          eventAction: 'feature_app_ready',
          eventName: `${__FEATURE_APP_NAME__} - Feature App ready`,
        },
      },
    };
    trackingService.track(readyEvent);
  }, []);

  const content = mapContent(rawContent);
  console.log('🚀 ~ map content:', content);
  contentService?.onContentChange(setRawContent as ContentChangeListener);

  useEffect(() => {
    if (!content && Object.keys(content).length === 0) return;

    // eslint-disable-next-line
    if (!content.priceBreakdownGroup && location.hostname === 'localhost') {
      // mock data to work locally
      dispatch(setPriceBreakdowns(mapContent(mockContentFromHeadless as FalconContent)));
      return;
    }

    dispatch(setPriceBreakdowns(content));
  }, [dispatch, content]);

  useEffect(() => {
    const cyUpdateContentHandler = (e: Event) => {
      // eslint-disable-next-line
      dispatch(setPriceBreakdowns(mapContent((e as CustomEvent)?.detail)));
    };
    if (
      typeof window !== 'undefined' &&
      // eslint-disable-next-line
      (!!window.Cypress || !!window.parent.Cypress || location.hostname === 'localhost')
    ) {
      window.addEventListener('CyUpdateContent', cyUpdateContentHandler);
    }
    return () => window.removeEventListener('CyUpdateContent', cyUpdateContentHandler);
  }, []);

  return (
    <I18nextProvider i18n={i18n}>
      {/* @ts-ignore:next-line */}
      <ErrorBoundary>
        <ThemeProvider theme={COLORS}>
          <ApolloProvider client={apolloClient}>
            <VehicleProvider isTier3={IS_T3_WEBSITE}>
              <div className="resetWrapper">
                <ContentWrapper />
              </div>
            </VehicleProvider>
          </ApolloProvider>
          <GlobalStyles />
        </ThemeProvider>
      </ErrorBoundary>
    </I18nextProvider>
  );
};

export default FeatureApp;
